// Task Deletion Utilities
// Pure functions for calculating deletion-related operations

import { getTaskWithSubtasks, getAllSubtaskIds } from './taskUtils';

/**
 * Determines the appropriate deletion mode based on task structure
 * @param {string} taskId - The task ID to delete
 * @param {Array} tasks - All tasks
 * @param {string|null} providedMode - User-provided deletion mode
 * @returns {string} - 'task-only' or 'task-and-subtasks'
 */
export function determineDeletionMode(taskId, tasks, providedMode = null) {
  const taskWithSubtasks = getTaskWithSubtasks(taskId, tasks);
  const hasSubtasks = taskWithSubtasks && taskWithSubtasks.subtasks && taskWithSubtasks.subtasks.length > 0;
  
  if (!hasSubtasks) {
    return 'task-only';
  }
  
  return providedMode || 'task-only';
}

/**
 * Calculates which siblings need their order shifted when a task is deleted
 * @param {Object} currentTask - The task being deleted
 * @param {Array} directSubtasks - Direct subtasks of the task being deleted
 * @param {Array} allTasks - All tasks
 * @returns {Array} - Array of { id, newOrder } objects for tasks that need order updates
 */
export function calculateSiblingOrderShifts(currentTask, directSubtasks, allTasks) {
  const newParentId = currentTask.parent_task_id;
  
  const newSiblings = allTasks.filter(t => {
    if (newParentId === null) {
      return t.parent_task_id === null && t.id !== currentTask.id;
    }
    return t.parent_task_id === newParentId && t.id !== currentTask.id;
  });
  
  newSiblings.sort((a, b) => {
    const orderA = a.order !== null && a.order !== undefined ? a.order : Number.MAX_SAFE_INTEGER;
    const orderB = b.order !== null && b.order !== undefined ? b.order : Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    return (a.created_at || '').localeCompare(b.created_at || '');
  });
  
  const currentTaskOrder = currentTask.order !== null && currentTask.order !== undefined 
    ? currentTask.order 
    : 0;
  
  const siblingsToShift = newSiblings.filter(sibling => {
    const siblingOrder = sibling.order !== null && sibling.order !== undefined
      ? sibling.order
      : Number.MAX_SAFE_INTEGER;
    return siblingOrder > currentTaskOrder && siblingOrder <= currentTaskOrder + directSubtasks.length;
  });
  
  return siblingsToShift.map(sibling => ({
    id: sibling.id,
    newOrder: sibling.order + directSubtasks.length
  }));
}

/**
 * Calculates the new order for reparented subtasks
 * @param {Object} currentTask - The task being deleted
 * @param {Array} directSubtasks - Direct subtasks to reparent
 * @returns {Array} - Array of { subtask, newOrder } objects
 */
export function calculateSubtaskReparentingOrders(currentTask, directSubtasks) {
  const currentTaskOrder = currentTask.order !== null && currentTask.order !== undefined 
    ? currentTask.order 
    : 0;
  const startingOrder = currentTaskOrder + 1;
  
  return directSubtasks.map((subtask, i) => ({
    subtask,
    newOrder: startingOrder + i
  }));
}

/**
 * Finds the target task to focus after deletion
 * @param {string} deletedTaskId - The ID of the deleted task
 * @param {Array} tasks - All tasks
 * @param {Set} expandedTasks - Set of expanded task IDs
 * @returns {Object|null} - The target task to focus, or null
 */
export function findTargetTaskAfterDeletion(deletedTaskId, tasks, expandedTasks) {
  const { buildTaskTree, flattenTaskTree } = require('./taskUtils');
  const taskTree = buildTaskTree(tasks);
  const flattenedTasks = flattenTaskTree(taskTree, expandedTasks);
  const currentIndex = flattenedTasks.findIndex(t => t.id === deletedTaskId);
  
  if (currentIndex > 0) {
    return flattenedTasks[currentIndex - 1];
  } else if (flattenedTasks.length > 1) {
    return flattenedTasks[1];
  }
  
  return null;
}
